import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DoorOpen, Phone, Mail, CalendarClock, LogOut, BedDouble, Building2, MapPin, Send, CheckCircle2,
} from "lucide-react";

const STATUS_COLORS = {
  disponible: { color: "#22c55e", bg: "rgba(34,197,94,0.14)" },
  bientot: { color: "#f59e0b", bg: "rgba(245,158,11,0.14)" },
  preparation: { color: "#0ea5e9", bg: "rgba(14,165,233,0.14)" },
  reservee: { color: "#a855f7", bg: "rgba(168,85,247,0.14)" },
};

export default function EspacePartenaires() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null); // { room, type }
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const statusLabel = (k) => t(`partner_portal.status.${k}`, { defaultValue: k });
  const typeLabel = (k) => t(`partner_portal.roomType.${k}`, { defaultValue: t("partner_portal.room") });

  const load = async () => {
    setLoading(true);
    try {
      const [i, r] = await Promise.all([api.get("/crm/partner/info"), api.get("/crm/partner/rooms")]);
      setInfo(i.data); setRooms(r.data || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openReq = (room, type) => { setDlg({ room, type }); setMessage(""); };
  const submitReq = async () => {
    setSending(true);
    try {
      await api.post("/crm/partner/requests", {
        room_id: dlg.room?.id || "",
        room_label: dlg.room ? `Ch. ${dlg.room.number || "?"} — ${typeLabel(dlg.room.room_type)}` : "",
        type: dlg.type,
        message: message.trim(),
      });
      toast.success(t("partner_portal.requestSent"));
      setDlg(null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setSending(false);
  };

  const lang = i18n.language || "fr";
  const locale = lang.startsWith("nl") ? "nl-BE" : lang.startsWith("en") ? "en-GB" : "fr-BE";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className="min-h-screen app-bg text-foreground" data-testid="partner-portal">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="h-10 w-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}><DoorOpen className="h-5 w-5" /></span>
            <div className="min-w-0">
              <h1 className="font-display font-bold leading-tight truncate" data-testid="partner-residence-name">{info?.residence_name || t("partner_portal.title")}</h1>
              <p className="text-xs text-muted-foreground truncate">{t("partner_portal.subtitle")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <LanguageSwitcher />
            <Button variant="ghost" size="sm" onClick={logout} data-testid="partner-logout" className="gap-1.5"><LogOut className="h-4 w-4" /><span className="hidden sm:inline">{t("partner_portal.logout")}</span></Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-7">
        {/* Bienvenue + contact */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-emerald-300">{t("partner_portal.welcome")}{info?.organization ? ` · ${info.organization}` : ""}</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-1">{user?.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("partner_portal.intro")}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {info?.contact_phone && (
              <a href={`tel:${info.contact_phone.replace(/\s/g, "")}`} data-testid="partner-call" className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition">
                <Phone className="h-4 w-4" /> {t("partner_portal.call")}
              </a>
            )}
            {info?.contact_email && (
              <a href={`mailto:${info.contact_email}`} className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 border border-white/15 hover:border-emerald-500 text-sm font-semibold transition">
                <Mail className="h-4 w-4" /> {t("partner_portal.writeEmail")}
              </a>
            )}
          </div>
        </div>

        {/* Chambres */}
        <div className="mt-7">
          {loading && <p className="text-muted-foreground py-10 text-center">{t("partner_portal.loading")}</p>}
          {!loading && rooms.length === 0 && (
            <div className="glass rounded-2xl p-10 text-center" data-testid="partner-no-rooms">
              <BedDouble className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="font-semibold">{t("partner_portal.noRooms")}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("partner_portal.noRoomsHint")}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rooms.map((r, idx) => {
              const cfg = STATUS_COLORS[r.commercial_status] || STATUS_COLORS.disponible;
              return (
                <motion.div key={r.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(idx * 0.04, 0.4) }}
                  className="glass rounded-2xl card-3d p-5 flex flex-col" data-testid="partner-room-card">
                  <div className="flex items-center justify-between">
                    <span className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: cfg.bg, color: cfg.color }}><BedDouble className="h-5 w-5" /></span>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: cfg.bg, color: cfg.color }}>{statusLabel(r.commercial_status)}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold mt-3">{typeLabel(r.room_type)}</h3>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {r.floor && <p className="flex items-center gap-2"><Building2 className="h-4 w-4" /> {t("partner_portal.floor")} : <span className="text-foreground/90">{r.floor}</span></p>}
                    {r.number && <p className="flex items-center gap-2"><MapPin className="h-4 w-4" /> {t("partner_portal.roomRef")} : <span className="text-foreground/90">{r.number}</span></p>}
                    <p className="flex items-center gap-2"><CalendarClock className="h-4 w-4" />
                      {r.commercial_status === "disponible" ? t("partner_portal.availImmediate") : r.available_from ? t("partner_portal.availFrom", { date: fmtDate(r.available_from) }) : t("partner_portal.availSoon")}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-white/10 flex gap-2">
                    {r.visit_possible && (
                      <Button size="sm" onClick={() => openReq(r, "visite")} data-testid="partner-request-visit" className="flex-1 bg-emerald-600 hover:bg-emerald-500 gap-1.5"><CalendarClock className="h-4 w-4" /> {t("partner_portal.requestVisit")}</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openReq(r, "message")} data-testid="partner-request-message" className="flex-1 gap-1.5"><Send className="h-4 w-4" /> {t("partner_portal.message")}</Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-10">
          {t("partner_portal.footer")}
        </p>
      </main>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="partner-request-dialog">
          <DialogHeader>
            <DialogTitle>{dlg?.type === "visite" ? t("partner_portal.requestVisit") : t("partner_portal.sendMessage")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {dlg?.room && (
              <div className="rounded-xl border border-white/10 p-3 text-sm">
                <span className="text-muted-foreground">{t("partner_portal.roomConcerned")} : </span>
                <span className="font-semibold">Ch. {dlg.room.number || "?"} · {typeLabel(dlg.room.room_type)}{dlg.room.floor ? ` · ${dlg.room.floor}` : ""}</span>
              </div>
            )}
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={dlg?.type === "visite" ? t("partner_portal.visitPlaceholder") : t("partner_portal.messagePlaceholder")}
              data-testid="partner-request-text"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlg(null)}>{t("partner_portal.cancel")}</Button>
            <Button onClick={submitReq} disabled={sending} data-testid="partner-request-submit" className="bg-emerald-600 hover:bg-emerald-500 gap-1.5">
              {sending ? t("partner_portal.sending") : <><CheckCircle2 className="h-4 w-4" /> {t("partner_portal.sendRequest")}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
